import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * PawsomeVibe Cart Drawer Web Component
 * Custom element for the PawsomeVibe-branded slide-out cart drawer.
 * Handles Ajax cart operations, free shipping banner, and cross-sell.
 * Only opens on explicit header icon click — NOT on add-to-cart.
 */
class CartDrawerPawsome extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.closeButtons = this.querySelectorAll('[data-action="close"]');
    this.overlay = this.querySelector('.pawsome-cart-drawer__overlay');
    this.panel = this.querySelector('.pawsome-cart-drawer__panel');
    this.cartBody = this.querySelector('[data-cart-body]');
    this.cartItems = this.querySelector('[data-cart-items]');
    this.cartCountLabel = this.querySelector('[data-cart-count-label]');
    this.cartTotal = this.querySelector('[data-cart-total]');
    this.shippingBanner = this.querySelector('[data-shipping-banner]');
    this.shippingMessage = this.querySelector('[data-shipping-message]');
    this.cartFooter = this.querySelector('[data-cart-footer]');
    this.crossSell = this.querySelector('[data-cross-sell]');
    // Money formatting & copy come from data attributes set by the section (shop-aware).
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.moneyCurrency = this.dataset.moneyCurrency || 'USD';
    this.emptyText = this.dataset.emptyText || 'Your cart is currently empty';
    this.emptyButtonLabel = this.dataset.emptyButtonLabel || 'Shop Best Sellers';
    this.emptyLink = this.dataset.emptyLink || '/collections/all';
    this.autoOpen = this.hasAttribute('auto-open');

    this._bindEvents();

    // Reflect the initial (server-rendered) cart state:
    // `[data-cart-items]` only exists server-side when the cart is not empty,
    // so hide the footer & cross-sell when it is absent.
    this._setEmptyVisibility(!this.cartItems);
    this._updateShippingProgress();
  }

  disconnectedCallback() {
    this.abortController.abort();
    document.body.classList.remove('pawsome-cart-drawer--open');
  }

  _bindEvents() {
    const signal = this.abortController.signal;

    // Close buttons & overlay
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close(), { signal });
    });
    this.overlay?.addEventListener('click', () => this.close(), { signal });

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    }, { signal });

    // Quantity change buttons
    this.addEventListener('click', (e) => {
      const target = e.target.closest('[data-qty-change]');
      if (target) {
        const key = target.dataset.key;
        const line = parseInt(target.dataset.line);
        const change = target.dataset.qtyChange;
        const input = target.closest('.pawsome-cart-drawer__quantity')?.querySelector('[data-qty-input]');
        const currentQty = input ? parseInt(input.value) : 1;
        const newQty = change === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        this._updateCartItem(line, key, newQty);
      }
    }, { signal });

    // Quantity input change
    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (input) {
        const key = input.dataset.key;
        const line = parseInt(input.dataset.line);
        const qty = parseInt(input.value) || 0;
        this._updateCartItem(line, key, qty);
      }
    }, { signal });

    // Remove item
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        const key = removeBtn.dataset.key;
        const line = parseInt(removeBtn.dataset.line);
        this._updateCartItem(line, key, 0);
      }
    }, { signal });

    // Cross-sell add
    this.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-add-cross-sell]');
      if (addBtn) {
        const variantId = parseInt(addBtn.dataset.variantId);
        this._addCrossSellItem(variantId, addBtn);
      }
    }, { signal });

    // Listen for CartAddEvent from theme — refresh cart, and auto-open only if the drawer's
    // `auto_open` setting is enabled. Never opens the default theme drawer.
    document.addEventListener(CartAddEvent.eventName, () => {
      this._refreshCart();
      if (this.autoOpen && !this.isOpen) this.open();
    }, { signal });

    // Listen for custom event to open drawer from header icon click
    document.addEventListener('pawsome:open-cart', () => {
      this._refreshCart();
      if (!this.isOpen) this.open();
    }, { signal });
  }

  open() {
    this.isOpen = true;
    this.classList.add('pawsome-cart-drawer--open');
    document.body.classList.add('pawsome-cart-drawer--open');
    this.panel?.focus();
  }

  close() {
    this.isOpen = false;
    this.classList.remove('pawsome-cart-drawer--open');
    document.body.classList.remove('pawsome-cart-drawer--open');
  }

  async _updateCartItem(line, key, quantity) {
    const item = this.querySelector(`[data-cart-item="${key}"]`);
    item?.classList.add('pawsome-cart-drawer__item--updating');

    try {
      if (quantity === 0) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!response.ok) throw new Error('Failed to remove item');
      } else {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!response.ok) throw new Error('Failed to update quantity');
      }

      await this._refreshCart();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('Cart update error:', error);
      item?.classList.remove('pawsome-cart-drawer__item--updating');
    }
  }

  async _addCrossSellItem(variantId, button) {
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) throw new Error('Failed to add item');

      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = '+ Add';
        button.disabled = false;
      }, 1500);

      await this._refreshCart();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('Cross-sell add error:', error);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = '+ Add';
        button.disabled = false;
      }, 2000);
    }
  }

  async _refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update header cart count badge
      this._updateHeaderCartBadge(cart.item_count);

      // Update cart count label inside drawer
      if (this.cartCountLabel) this.cartCountLabel.textContent = `(${cart.item_count})`;

      // Update total using the shop-aware money format
      if (this.cartTotal) {
        this.cartTotal.textContent = this._formatMoney(cart.total_price);
      }

      // Show/hide the footer & cross-sell depending on whether the cart has items
      this._setEmptyVisibility(cart.item_count === 0);

      // Re-render items or the empty state (both rebuild the DOM so refs stay attached)
      if (cart.item_count === 0) {
        this._showEmptyState();
      } else {
        this._renderCartItems(cart.items);
      }

      // Hide any cross-sell products that are already in the cart
      this._refreshCrossSell(cart.items);
    } catch (error) {
      console.error('Cart refresh error:', error);
    }
  }

  _updateHeaderCartBadge(count) {
    // Update the header cart bubble count text
    const headerBadge = document.querySelector('.header-actions__cart-icon .cart-bubble__text-count');
    if (headerBadge) {
      headerBadge.textContent = count;
    }

    // Update the cart-icon custom element's count attribute
    const cartIcon = document.querySelector('cart-icon');
    if (cartIcon) {
      cartIcon.setAttribute('item-count', count);
    }

    // Update header cart icon has-cart class
    const headerCartIcon = document.querySelector('.header-actions__cart-icon');
    if (headerCartIcon) {
      if (count > 0) {
        headerCartIcon.classList.add('header-actions__cart-icon--has-cart');
      } else {
        headerCartIcon.classList.remove('header-actions__cart-icon--has-cart');
      }
    }
  }

  _updateShippingProgress() {
    if (!this.shippingBanner) return;
    // Always show "FREE shipping unlocked" — no threshold logic
    const bannerText = this.shippingBanner.querySelector('.pawsome-cart-drawer__shipping-text');
    if (bannerText) {
      bannerText.textContent = '\uD83C\uDF89 You\'ve unlocked FREE US Shipping!';
    }
    if (this.shippingMessage) {
      this.shippingMessage.textContent = '\uD83D\uDE9A Free shipping on all orders!';
      this.shippingMessage.style.color = '#2E7D32';
    }
  }

  _showEmptyState() {
    if (!this.cartBody) return;
    this.cartBody.innerHTML = `
      <div class="pawsome-cart-drawer__empty">
        <div class="pawsome-cart-drawer__empty-icon" aria-hidden="true">&#128062;</div>
        <p class="pawsome-cart-drawer__empty-text">${this._escapeHtml(this.emptyText)}</p>
        <a href="${this._escapeHtml(this.emptyLink)}" class="pawsome-cart-drawer__empty-cta pawsome-pill-button">
          ${this._escapeHtml(this.emptyButtonLabel)} &rarr;
        </a>
      </div>
    `;
    // The items container was replaced by the empty state, so clear the cached ref.
    // It will be recreated (and re-attached) the next time items are rendered.
    this.cartItems = null;
  }

  _renderCartItems(items) {
    if (!this.cartBody) return;

    // Always build a fresh <div data-cart-items> inside the body and re-point the cached ref.
    // Previously the container was cached once and reused — after the empty state replaced the
    // body's innerHTML that cached node became detached, so re-rendered items never appeared.
    const container = document.createElement('div');
    container.className = 'pawsome-cart-drawer__items';
    container.setAttribute('data-cart-items', '');

    container.innerHTML = items.map(item => {
      const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="pawsome-cart-drawer__item-variant">${this._escapeHtml(item.variant_title)}</p>`
        : '';
      const imageHtml = item.image
        ? `<img src="${this._escapeHtml(item.image)}" alt="${this._escapeHtml(item.product_title)}" loading="lazy" width="80" height="80">`
        : `<div class="pawsome-cart-drawer__item-placeholder"></div>`;

      return `
        <div class="pawsome-cart-drawer__item" data-cart-item="${this._escapeHtml(item.key)}" data-variant-id="${this._escapeHtml(String(item.variant_id))}">
          <div class="pawsome-cart-drawer__item-image">
            <a href="${this._escapeHtml(item.url)}">${imageHtml}</a>
          </div>
          <div class="pawsome-cart-drawer__item-details">
            <a href="${this._escapeHtml(item.url)}" class="pawsome-cart-drawer__item-name">${this._escapeHtml(item.product_title)}</a>
            ${variantHtml}
            <p class="pawsome-cart-drawer__item-price">${this._formatMoney(item.final_price)}</p>
            <div class="pawsome-cart-drawer__item-actions">
              <div class="pawsome-cart-drawer__quantity">
                <button class="pawsome-cart-drawer__qty-btn" data-qty-change="decrease" data-line="${item.line}" data-key="${this._escapeHtml(item.key)}" aria-label="Decrease quantity">-</button>
                <input class="pawsome-cart-drawer__qty-input" type="number" value="${item.quantity}" min="0" data-line="${item.line}" data-key="${this._escapeHtml(item.key)}" aria-label="Quantity" data-qty-input>
                <button class="pawsome-cart-drawer__qty-btn" data-qty-change="increase" data-line="${item.line}" data-key="${this._escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
              </div>
              <button class="pawsome-cart-drawer__remove" data-remove-item data-line="${item.line}" data-key="${this._escapeHtml(item.key)}" aria-label="Remove item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    this.cartBody.innerHTML = '';
    this.cartBody.appendChild(container);
    // Re-point the cached ref to the attached node so later renders target live DOM.
    this.cartItems = container;
  }

  /**
   * Toggles the visibility of the footer (subtotal + checkout) and the cross-sell
   * section so they are hidden when the cart is empty.
   * @param {boolean} isEmpty
   */
  _setEmptyVisibility(isEmpty) {
    if (this.cartFooter) this.cartFooter.style.display = isEmpty ? 'none' : '';
    if (this.crossSell) this.crossSell.style.display = isEmpty ? 'none' : '';
  }

  /**
   * Hides cross-sell products whose variant is already present in the cart.
   * @param {Array<{variant_id: number}>} cartItems
   */
  _refreshCrossSell(cartItems) {
    if (!this.crossSell) return;
    const inCartVariantIds = new Set((cartItems || []).map(item => String(item.variant_id)));
    let visibleCount = 0;
    this.querySelectorAll('[data-cross-sell-product]').forEach(el => {
      const variantId = el.dataset.crossSellProduct;
      const hidden = Boolean(variantId && inCartVariantIds.has(variantId));
      el.style.display = hidden ? 'none' : '';
      if (!hidden) visibleCount += 1;
    });
    // B4.3: moi item deu da co trong cart -> an ca khoi (header 'PAIRS WELL WITH' moi coi)
    const hasItems = (cartItems || []).length > 0;
    this.crossSell.style.display = (visibleCount === 0 || !hasItems) ? 'none' : '';
  }

  _formatMoney(cents) {
    return formatMoney(cents, this.moneyFormat || '{{amount}}', this.moneyCurrency || 'USD');
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-drawer-pawsome')) {
  customElements.define('cart-drawer-pawsome', CartDrawerPawsome);
}